import { track } from "@/lib/analytics";
import type { FoodItem } from "@/components/FoodCard";
import { Geolocation } from '@capacitor/geolocation';
// shareTargets imported dynamically to avoid circular dependency
import { cacheImage, cacheImageById, getCachedImage, getCachedImageById } from "@/lib/imageCache";

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface SavedDish {
  id: string;
  menuId: string;
  canonicalName: string; // normalized dish name for cross-restaurant matching
  name: string;
  description: string;
  /** Protein/size choices for a grouped variant dish (e.g. "chicken 105kr, beef 109kr"). */
  variantOptions?: string;
  price?: string;
  imageUrl?: string;
  dietaryTags?: string[];
  originalName?: string;
  detectedLanguage?: string;
  allergens?: string[];
  currency?: string;
  nutrition?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
  };
  restaurantName: string;
  orderedAt: string; // ISO date
  rating?: "loved" | "not-for-me";
  ratedAt?: string;
  location?: {
    lat: number;
    lng: number;
    label?: string; // reverse-geocoded or manual
  };
  userPhotos?: string[]; // IndexedDB keys for user-taken photos
}

export interface SavedMenu {
  id: string;
  restaurantName: string;
  date: string;
  items: FoodItem[];
  detectedLanguage?: string;
  location?: {
    lat: number;
    lng: number;
    label?: string;
  };
  selectedDishIds?: string[]; // IDs of dishes user ordered
  orderedDishIds?: string[]; // dishes marked as "ordered" at the restaurant
  scanTimestamp?: number; // unix ms — when the scan happened, for post-meal notification
  dishRatings?: Record<string, 'slaps' | 'mid' | 'skip'>; // ratings keyed by dish name
  mood?: string; // mood tag from share flow (fire, cozy, bougie, etc.)
}

// ─── Keys ───────────────────────────────────────────────────────────────────────

const DISHES_KEY = "carte-lens-dishes";
const MENUS_KEY = "carte-lens-history"; // keep backward compat with MenuHistory key
const PENDING_RATINGS_KEY = "carte-lens-pending-ratings";

// ─── ID Generator ───────────────────────────────────────────────────────────────

export function generateId(prefix: string = "id"): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Dishes ─────────────────────────────────────────────────────────────────────


// Normalize a dish name for cross-restaurant matching (same logic as imageCache.ts)
function normalizeDishName(name: string): string {
  return (name || "").toLowerCase().trim().replace(/\s+/g, " ")
    .replace(/œ/g, "oe")
    .replace(/[éèêë]/g, "e")
    .replace(/[áàâäå]/g, "a")
    .replace(/[íìîï]/g, "i")
    .replace(/[óòôöø]/g, "o")
    .replace(/[úùûü]/g, "u")
    .replace(/[ç]/g, "c")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b(grilled|grillad|roasted|seared|fried|crispy|homemade|house|signature|classic|fresh)\b/g, "")
    .replace(/\b(with|served|and|the|a|an|of|in|on)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Strip large base64 image URLs to prevent localStorage quota exceeded
// Before stripping, cache the image in IndexedDB by both canonical name and dishId for recovery
function stripLargeData(dish: SavedDish): SavedDish {
  if (dish.imageUrl && dish.imageUrl.length > 500) {
    // Cache image by both canonical name and dishId before stripping
    const canonical = dish.canonicalName || normalizeDishName(dish.name);
    cacheImage(canonical, dish.imageUrl).catch(() => {});
    if (dish.id) cacheImageById(dish.id, dish.imageUrl).catch(() => {});
  }
  return {
    ...dish,
    imageUrl: dish.imageUrl && dish.imageUrl.length > 500 ? undefined : dish.imageUrl,
    description: dish.description && dish.description.length > 500 ? dish.description.slice(0, 500) + "..." : dish.description,
  };
}

// Internal sync loader — always sync, no side effects
function loadDishesOnce(): SavedDish[] {
  try {
    const raw = localStorage.getItem(DISHES_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return [];
}

// Public loader — simple direct pass-through (original working version)
export function loadDishes(): SavedDish[] {
  const dishes = loadDishesOnce();
  return dishes;
}

// Call this explicitly on app init or user action — not on every loadDishes()
let _hasRefreshed = false;
export function refreshDishImagesOnce(): void {
  if (_hasRefreshed) return;
  _hasRefreshed = true;
  void refreshDishImages();
}

// Background rehydration — silently updates storage when IndexedDB has images missing in localStorage (no-ui)
export function refreshDishImages(): void {
  loadDishesOnce().forEach(async (dish) => {
    if (dish.imageUrl || !dish.id) return;
    try {
      const cached = await getCachedImageById(dish.id);
      if (cached) { updateDish(dish.id, { imageUrl: cached }); return; }
      if (dish.name) {
        const byName = await getCachedImage(dish.name);
        if (byName) updateDish(dish.id, { imageUrl: byName });
      }
    } catch (_e) {}
  });
}


// Safe localStorage write — handles QuotaExceeded gracefully
function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (_e) {
    // Try to free space by trimming old data
    try {
      const dishes = JSON.parse(localStorage.getItem("carte-lens-dishes") || "[]");
      if (dishes.length > 50) {
        localStorage.setItem("carte-lens-dishes", JSON.stringify(dishes.slice(0, 50)));
      }
      const menus = JSON.parse(localStorage.getItem(MENUS_KEY) || "[]");
      if (menus.length > 15) {
        localStorage.setItem(MENUS_KEY, JSON.stringify(menus.slice(0, 15)));
      }
      // Retry the write
      localStorage.setItem(key, value);
      return true;
    } catch (_e2) {
      return false;
    }
  }
}

export function saveDish(dish: SavedDish): void {
  const dishes = loadDishes();
  const withCanonical = { ...dish, canonicalName: dish.canonicalName || normalizeDishName(dish.name) };
  dishes.unshift(stripLargeData(withCanonical));
  if (dishes.length > 100) dishes.length = 100;
  safeSetItem(DISHES_KEY, JSON.stringify(dishes));
  track("dish_saved", { dishName: dish.name, restaurantName: dish.restaurantName, hasImage: !!dish.imageUrl });
}


export function saveDishes(newDishes: SavedDish[]): void {
  const dishes = loadDishes();
  dishes.unshift(...newDishes.map(d => stripLargeData({ ...d, canonicalName: d.canonicalName || normalizeDishName(d.name) })));
  if (dishes.length > 100) dishes.length = 100;
  safeSetItem(DISHES_KEY, JSON.stringify(dishes));
  track("dishes_saved_batch", { count: newDishes.length, restaurantName: newDishes[0]?.restaurantName });
}

export function getDish(id: string): SavedDish | undefined {
  return loadDishes().find((d) => d.id === id);
}

export function updateDish(id: string, updates: Partial<SavedDish>): void {
  const dishes = loadDishes();
  const idx = dishes.findIndex((d) => d.id === id);
  if (idx >= 0) {
    dishes[idx] = { ...dishes[idx], ...updates };
    safeSetItem(DISHES_KEY, JSON.stringify(dishes));
    // Wave N — mirror the rating up to the anonymous leaderboard (best-effort).
    if (updates.rating) {
      const d = dishes[idx];
      void import("@/lib/ratingsSync").then((m) =>
        m.syncRating({
          dishName: d.name,
          restaurantName: d.restaurantName,
          rating: m.scoreFromDishRating(updates.rating as "loved" | "not-for-me"),
          source: "dish",
        }),
      ).catch(() => {});
    }
  }
}

export function deleteDish(id: string): void {
  const dishes = loadDishes().filter((d) => d.id !== id);
  safeSetItem(DISHES_KEY, JSON.stringify(dishes));
}

export function getDishesByMenu(menuId: string): SavedDish[] {
  return loadDishes().filter((d) => d.menuId === menuId);
}

// ─── Menus ──────────────────────────────────────────────────────────────────────

export function loadMenus(): SavedMenu[] {
  try {
    const raw = localStorage.getItem(MENUS_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {}
  return [];
}

export function saveMenu(menu: SavedMenu): void {
  const menus = loadMenus();
  menus.unshift(menu);
  if (menus.length > 20) menus.length = 20;
  safeSetItem(MENUS_KEY, JSON.stringify(menus.map(m => ({...m, items: m.items?.map(i => ({...i, imageUrl: i.imageUrl && i.imageUrl.length < 500 ? i.imageUrl : undefined}))}))));
  track("menu_scanned", { restaurantName: menu.restaurantName, itemCount: menu.items?.length || 0, hasLocation: !!menu.location?.label });
}

export function updateMenu(id: string, updates: Partial<SavedMenu>): void {
  const menus = loadMenus();
  const idx = menus.findIndex((m) => m.id === id);
  if (idx >= 0) {
    menus[idx] = { ...menus[idx], ...updates };
    safeSetItem(MENUS_KEY, JSON.stringify(menus.map(m => ({...m, items: m.items?.map(i => ({...i, imageUrl: i.imageUrl && i.imageUrl.length < 500 ? i.imageUrl : undefined}))}))));
    // Wave N — mirror dish ratings up to the anonymous leaderboard (best-effort).
    if (updates.dishRatings) {
      const restaurantName = menus[idx].restaurantName;
      const ratings = updates.dishRatings;
      void import("@/lib/ratingsSync").then((m) => {
        for (const [dishName, val] of Object.entries(ratings)) {
          m.syncRating({
            dishName,
            restaurantName,
            rating: m.scoreFromMenuRating(val),
            source: "menu",
          });
        }
      }).catch(() => {});
    }
  }
}

export function getMenu(id: string): SavedMenu | undefined {
  return loadMenus().find((m) => m.id === id);
}

export function deleteMenu(id: string): void {
  const menus = loadMenus().filter((m) => m.id !== id);
  safeSetItem(MENUS_KEY, JSON.stringify(menus.map(m => ({...m, items: m.items?.map(i => ({...i, imageUrl: i.imageUrl && i.imageUrl.length < 500 ? i.imageUrl : undefined}))}))));
}

// ─── Pending Ratings (1-hour reminder) ──────────────────────────────────────────

export interface PendingRating {
  dishId: string;
  dishName: string;
  savedAt: number; // timestamp ms
  prompted: boolean;
}

export function loadPendingRatings(): PendingRating[] {
  try {
    const raw = localStorage.getItem(PENDING_RATINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return [];
}

export function addPendingRatings(dishes: { dishId: string; dishName: string }[]): void {
  const existing = loadPendingRatings();
  const now = Date.now();
  const newEntries: PendingRating[] = dishes.map((d) => ({
    dishId: d.dishId,
    dishName: d.dishName,
    savedAt: now,
    prompted: false,
  }));
  existing.unshift(...newEntries);
  safeSetItem(PENDING_RATINGS_KEY, JSON.stringify(existing));
  track("rating_reminder_scheduled", { count: dishes.length });
}

export function getUnratedDishesAfterOneHour(): PendingRating[] {
  const pending = loadPendingRatings();
  const now = Date.now();
  const ONE_HOUR = 60 * 60 * 1000;
  return pending.filter((p) => !p.prompted && now - p.savedAt >= ONE_HOUR);
}

export function markPendingAsPrompted(dishIds: string[]): void {
  const pending = loadPendingRatings();
  const set = new Set(dishIds);
  pending.forEach((p) => {
    if (set.has(p.dishId)) p.prompted = true;
  });
  safeSetItem(PENDING_RATINGS_KEY, JSON.stringify(pending));
}

export function clearPromptedRatings(): void {
  const pending = loadPendingRatings().filter((p) => !p.prompted);
  safeSetItem(PENDING_RATINGS_KEY, JSON.stringify(pending));
}

// ─── Location helpers ───────────────────────────────────────────────────────────

export async function getCurrentLocation(): Promise<{ lat: number; lng: number }> {
  // Try Capacitor Geolocation plugin first (works in native iOS/Android webview)
  try {
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });
    return {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
  } catch (e) {
    // Capacitor plugin not available or denied — fall back to browser API
  }

  // Fallback to browser Geolocation API
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  });
}

export function getGoogleMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`,
      { headers: { "User-Agent": "CarteLens/1.0" } }
    );
    if (!res.ok) throw new Error("Geocode failed");
    const data = await res.json();
    const addr = data.address;
    if (addr) {
      const parts = [
        addr.road || addr.pedestrian || addr.neighbourhood,
        addr.city || addr.town || addr.village || addr.suburb,
      ].filter(Boolean);
      if (parts.length > 0) return parts.join(", ");
    }
    return data.display_name?.split(",").slice(0, 2).join(",") || "Unknown location";
  } catch (e) {
    return `Near ${lat.toFixed(2)}°, ${lng.toFixed(2)}°`;
  }
}

// ─── Share helpers ──────────────────────────────────────────────────────────────

export async function shareDish(dish: SavedDish): Promise<void> {
  const { prepareSharePayload, sharePreparedPayload } = await import("@/lib/shareTargets");
  const payload = await prepareSharePayload("system", {
    restaurantName: dish.restaurantName,
    items: [
      {
        name: dish.name,
        imageUrl: dish.imageUrl,
        price: dish.price,
        description: dish.description,
      },
    ],
    shareMode: "card-only",
    locationLabel: dish.location?.label,
    dishName: dish.name,
    rating: dish.rating,
  });

  await sharePreparedPayload(payload);
}

export async function shareDishes(dishes: SavedDish[], restaurantName: string): Promise<void> {
  const { prepareSharePayload, sharePreparedPayload } = await import("@/lib/shareTargets");
  const payload = await prepareSharePayload("system", {
    restaurantName,
    items: dishes.map((dish) => ({
      name: dish.name,
      imageUrl: dish.imageUrl,
      price: dish.price,
      description: dish.description,
    })),
    shareMode: dishes.length > 1 ? "selected-cards" : "card-only",
    locationLabel: dishes[0]?.location?.label,
  });

  await sharePreparedPayload(payload);
}
